import React, { useState } from 'react';
import { Tags, Search, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useInterests, Interest } from '@/hooks/useInterests';
import { cn } from '@/lib/utils';

interface ArenaTagSelectorProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  maxTags?: number;
}

export const ArenaTagSelector: React.FC<ArenaTagSelectorProps> = ({
  selectedTags,
  onTagsChange,
  maxTags = 5,
}) => {
  const { interests, interestsByCategory, loading } = useInterests();
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const toggleTag = (interestId: string) => {
    if (selectedTags.includes(interestId)) {
      onTagsChange(selectedTags.filter((id) => id !== interestId));
    } else if (selectedTags.length < maxTags) {
      onTagsChange([...selectedTags, interestId]);
    }
  };

  const getSelectedInterests = () =>
    interests.filter((i) => selectedTags.includes(i.id));

  const filteredInterests = searchQuery
    ? interests.filter((i) =>
        i.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium flex items-center gap-2">
          <Tags className="w-4 h-4" />
          Topics (optional)
        </label>
        <span className="text-xs text-muted-foreground">
          {selectedTags.length}/{maxTags}
        </span>
      </div>

      {/* Selected tags */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {getSelectedInterests().map((interest) => (
            <Badge
              key={interest.id}
              variant="secondary"
              className="gap-1 cursor-pointer hover:bg-destructive/10"
              onClick={() => toggleTag(interest.id)}
            >
              {interest.name}
              <X className="w-3 h-3" />
            </Badge>
          ))}
        </div>
      )}

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start text-muted-foreground"
            disabled={selectedTags.length >= maxTags}
          >
            <Tags className="w-4 h-4 mr-2" />
            {selectedTags.length === 0
              ? 'Add topics to help people discover your arena'
              : `Add more topics (${maxTags - selectedTags.length} left)`}
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="h-[70vh] rounded-t-3xl">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Tags className="w-5 h-5" />
              Select Topics
            </SheetTitle>
          </SheetHeader>

          <div className="mt-4 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <ScrollArea className="h-[calc(70vh-180px)]">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filteredInterests ? (
                // Search results
                <div className="flex flex-wrap gap-2 p-1">
                  {filteredInterests.map((interest) => (
                    <InterestChip
                      key={interest.id}
                      interest={interest}
                      isSelected={selectedTags.includes(interest.id)}
                      disabled={
                        selectedTags.length >= maxTags &&
                        !selectedTags.includes(interest.id)
                      }
                      onToggle={() => toggleTag(interest.id)}
                    />
                  ))}
                  {filteredInterests.length === 0 && (
                    <p className="text-sm text-muted-foreground py-4 text-center w-full">
                      No topics found
                    </p>
                  )}
                </div>
              ) : (
                // Grouped by category
                <div className="space-y-4">
                  {Object.entries(interestsByCategory).map(([category, items]) => (
                    <div key={category}>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                        {category}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {items.map((interest) => (
                          <InterestChip
                            key={interest.id}
                            interest={interest}
                            isSelected={selectedTags.includes(interest.id)}
                            disabled={
                              selectedTags.length >= maxTags &&
                              !selectedTags.includes(interest.id)
                            }
                            onToggle={() => toggleTag(interest.id)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            <div className="pt-2">
              <Button
                className="w-full"
                onClick={() => setIsOpen(false)}
              >
                Done ({selectedTags.length} selected)
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

interface InterestChipProps {
  interest: Interest;
  isSelected: boolean;
  disabled: boolean;
  onToggle: () => void;
}

const InterestChip: React.FC<InterestChipProps> = ({
  interest,
  isSelected,
  disabled,
  onToggle,
}) => (
  <button
    onClick={onToggle}
    disabled={disabled}
    className={cn(
      'flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-all',
      isSelected
        ? 'bg-primary text-primary-foreground'
        : 'bg-muted/50 text-foreground hover:bg-muted',
      disabled && !isSelected && 'opacity-50 cursor-not-allowed'
    )}
  >
    {isSelected && <Check className="w-3 h-3" />}
    {interest.name}
  </button>
);

export default ArenaTagSelector;
